import { NavLink, useNavigate } from "react-router-dom";
import { useAuth, useModuleVisibility } from "../App";
import { usePanels, useActivePanel } from "../hooks/usePanels";
import { ITEMS, PANEL_ICON_MAP, PANEL_COLORS } from "../config/panels";
import { useState, useEffect } from "react";
import {
  X,
  LogOut,
  Shield,
  MessageSquare,
  CreditCard,
  Globe,
  GraduationCap,
  LayoutGrid,
  ArrowLeft,
  ChevronRight,
  Database,
} from "lucide-react";
import { GlobalSearch, SearchButton } from "./GlobalSearch";
import { useTutorial } from "./TutorialGuide";
import GianlucaAvatar from "./GianlucaAvatar";
import StandortSwitcher from "./StandortSwitcher";

// Tutorial Button Komponente
const TutorialButton = () => {
  const tutorial = useTutorial();
  if (!tutorial) return null;
  const { startTutorial, progress, isActive, assistantName } = tutorial;
  if (isActive) return null;

  return (
    <div className="px-3 py-2 border-b border-slate-700/30">
      <button
        onClick={startTutorial}
        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-300 hover:text-violet-300 hover:bg-violet-500/10 rounded-lg transition-all group"
        data-testid="tutorial-start-btn"
      >
        <GianlucaAvatar size="xs" state="idle" />
        <span>{assistantName || "Gian-Luca"}</span>
        {progress > 0 && progress < 100 && (
          <span className="ml-auto text-xs bg-violet-500/20 text-violet-400 px-1.5 py-0.5 rounded">
            {progress}%
          </span>
        )}
        {progress === 0 && (
          <span className="ml-auto text-[10px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded font-medium">
            NEU
          </span>
        )}
      </button>
    </div>
  );
};

/** Renders the nav items of a single panel. */
const PanelNavItems = ({ panel, visibility, onClose }) => {
  const colour = PANEL_COLORS[panel.color] || PANEL_COLORS.slate;
  const items = panel.items
    .map((id) => ITEMS[id])
    .filter(Boolean)
    .filter((item) => item.key === null || visibility[item.key]);

  if (items.length === 0) {
    return <p className="px-4 py-3 text-xs text-slate-500 italic">Keine Module sichtbar</p>;
  }

  return (
    <div className="py-2">
      {items.map((item) => (
        <NavLink
          key={item.path}
          to={item.path.split("?")[0]}
          className={({ isActive }) => `nav-item ${isActive ? "active" : ""} ${item.highlight ? "ki-highlight" : ""}`}
          onClick={onClose}
          data-testid={`nav-${item.path.replace(/^\//, "").split("?")[0] || "dashboard"}`}
        >
          <item.icon size={16} className={item.highlight ? "text-violet-400" : colour.icon} />
          <span>{item.label}</span>
          {item.highlight && (
            <span className="ml-auto text-[10px] bg-violet-600 text-white px-1.5 py-0.5 rounded-full">NEU</span>
          )}
        </NavLink>
      ))}
    </div>
  );
};

/** Top-level list of panels (shown on /dashboard). */
const PanelsTopLevel = ({ panels, visibility, navigate, onClose }) => {
  const resolve = (panel) =>
    panel.items
      .map((id) => ITEMS[id])
      .filter(Boolean)
      .filter((item) => item.key === null || visibility[item.key]);

  return (
    <div className="py-2">
      {panels.map((panel) => {
        const Icon = PANEL_ICON_MAP[panel.icon] || PANEL_ICON_MAP.LayoutDashboard;
        const colour = PANEL_COLORS[panel.color] || PANEL_COLORS.slate;
        const items = resolve(panel);
        if (items.length === 0) return null;

        return (
          <button
            key={panel.id}
            type="button"
            onClick={() => {
              onClose?.();
              navigate(items[0].path.split("?")[0]);
            }}
            className="w-full nav-item group"
            data-testid={`sidebar-panel-${panel.id}`}
          >
            <Icon size={16} className={colour.icon} />
            <span className="flex-1 text-left">{panel.label}</span>
            <span className="text-[10px] text-slate-500 mr-1">{items.length}</span>
            <ChevronRight size={13} className="text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        );
      })}
    </div>
  );
};

export const Sidebar = ({ isOpen, onClose, user, onFeedbackOpen }) => {
  const { logout } = useAuth();
  const { visibility } = useModuleVisibility();
  const { panels } = usePanels();
  const activePanel = useActivePanel();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleLogout = async () => { await logout(); };
  const goToWebsite = () => { navigate("/"); };
  const goToPanels = () => { onClose?.(); navigate("/dashboard"); };

  const isAdmin = user?.role === "admin";

  const ActiveIcon = activePanel ? (PANEL_ICON_MAP[activePanel.icon] || PANEL_ICON_MAP.LayoutDashboard) : null;
  const activeColour = activePanel ? (PANEL_COLORS[activePanel.color] || PANEL_COLORS.slate) : null;

  return (
    <aside className={`sidebar ${isOpen ? "sidebar-open" : ""}`}>
      {/* Logo/Brand */}
      <div className="p-4 border-b border-slate-700/30 bg-gradient-to-r from-slate-900/50 to-slate-800/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="MargeVio" className="h-12 w-auto" />
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={goToWebsite}
              className="p-1.5 text-slate-400 hover:text-violet-400 hover:bg-slate-700/50 rounded-lg transition-all"
              title="Zur Website"
              data-testid="go-to-website-btn"
            >
              <Globe size={18} />
            </button>
            <button
              className="md:hidden btn-icon p-1 text-slate-300 hover:text-white"
              onClick={onClose}
              data-testid="close-sidebar-btn"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* User Profile */}
      {user && (
        <div className="px-3 py-2 border-b border-stone-100 dark:border-slate-700/30 bg-[#F9F8F6] dark:bg-slate-800/60">
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
              isAdmin ? "bg-blue-500 text-white" : "bg-[#3A4D39] dark:bg-violet-600 text-white"
            }`}>
              {user.name?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-[#1A1A1A] dark:text-white truncate" data-testid="user-name">
                {user.name}
              </p>
              {isAdmin && <p className="text-[9px] text-blue-400 font-medium">Admin</p>}
            </div>
          </div>
        </div>
      )}

      {/* Global Search */}
      <div className="px-3 py-2 border-b border-slate-700/30">
        <SearchButton onClick={() => setSearchOpen(true)} />
      </div>

      {/* Standort-Wechsler */}
      <StandortSwitcher />

      {/* Tutorial Button */}
      <TutorialButton />

      {/* Admin Link */}
      {isAdmin && (
        <div className="px-2 py-1 border-b border-stone-100 dark:border-slate-700/30 bg-violet-900/10">
          <p className="text-[10px] text-violet-400 uppercase tracking-wide font-semibold px-3 py-1">Admin</p>
          <NavLink to="/admin" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`} onClick={onClose} data-testid="nav-admin">
            <Shield size={16} />
            <span>Benutzer</span>
          </NavLink>
          <NavLink to="/admin/subscriptions" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`} onClick={onClose} data-testid="nav-admin-subscriptions">
            <CreditCard size={16} />
            <span>Abonnements</span>
          </NavLink>
          <NavLink to="/admin/backup" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`} onClick={onClose} data-testid="nav-admin-backup">
            <Database size={16} />
            <span>Backup</span>
          </NavLink>
        </div>
      )}

      {/* Panel-Header (always visible) */}
      <div className="px-2 pt-2">
        <button
          type="button"
          onClick={goToPanels}
          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-sm ${
            activePanel
              ? "text-slate-300 hover:text-white hover:bg-slate-800/60"
              : "bg-violet-500/10 text-violet-300 ring-1 ring-inset ring-violet-500/25"
          }`}
          data-testid="sidebar-panels-home"
        >
          {activePanel ? <ArrowLeft size={14} /> : <LayoutGrid size={14} />}
          <span className="font-medium">{activePanel ? "Panel-Übersicht" : "Alle Panels"}</span>
        </button>
      </div>

      {/* Active Panel Indicator */}
      {activePanel && (
        <div className="px-3 pt-3 pb-1">
          <div className="flex items-center gap-2">
            {ActiveIcon && <ActiveIcon size={14} className={activeColour.icon} />}
            <p className="text-[10.5px] uppercase tracking-[0.15em] font-semibold text-slate-400">
              {activePanel.label}
            </p>
            <span className={`w-1.5 h-1.5 rounded-full ${activeColour.dot}`} />
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto">
        {activePanel ? (
          <PanelNavItems panel={activePanel} visibility={visibility} onClose={onClose} />
        ) : (
          <PanelsTopLevel panels={panels} visibility={visibility} navigate={navigate} onClose={onClose} />
        )}
      </nav>

      {/* Feedback + Logout */}
      <div className="px-2 py-2 border-t border-stone-100 dark:border-slate-700/30 space-y-1">
        {!isAdmin && (
          <button
            onClick={() => { onFeedbackOpen?.(); onClose?.(); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-xs rounded"
            data-testid="feedback-btn"
          >
            <MessageSquare size={14} />
            <span>Feedback senden</span>
          </button>
        )}
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-2 text-[#C62828] dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-xs rounded"
          data-testid="logout-btn"
        >
          <LogOut size={14} />
          <span>Abmelden</span>
        </button>
      </div>

      {/* Footer */}
      <div className="px-3 py-2 border-t border-slate-700/30 bg-gradient-to-r from-slate-900/30 to-slate-800/30">
        <p className="text-[9px] text-slate-400 text-center">Gastro Kalkulator</p>
        <p className="text-[9px] text-violet-400 text-center font-medium">by Gian-Luca</p>
      </div>

      <GlobalSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </aside>
  );
};

export default Sidebar;
